import { Component, Input, OnInit } from "@angular/core";
import { NavigationEnd, Route, Router } from "@angular/router";
import * as $ from "jquery";
import * as _ from "lodash";

import { environment } from "../../../environments/environment";
import {
  DB2DataSourceAvailability,
  PrismDataSource,
} from "../../models/prism-data-source.model";
import { Section } from "../../models/section.model";
import * as routes from "../../routes";
import { AuthorizationService } from "../../services/authorization.service";
import { DepositSettingsService } from "../../services/depositSettings-api.service";
import { GlobalFilterService } from "../../services/global-filter.service";

declare function initializeHealthcareTopBarV2(
  parentContainer: JQuery,
  auth0ClientId: string,
  auth0Domain: string,
  auth0RefreshToken: boolean,
  auth0Scope: string,
  redirectUri?: string,
  PHILoggingToggleCallback?: (toggleStatus: string) => void
): Promise<any>;

const LOCKBOX_SECTION = "Deposit Confirmation";
const LOCKBOX = "LB";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.css"],
})
export class HeaderComponent implements OnInit {
  private topBarLoaded = false;
  private _clientSelections: any[];

  readonly rebrandDate: Date = new Date(environment.rebrandStartDate);
  public showNewLogo: boolean;
  public showEnvironmentMessage = false;
  private currentRoute: Route;

  @Input() sections: string;
  private sectionsObj: Section[];
  validSections = [];

  constructor(
    private globalFilterService: GlobalFilterService,
    private authorizationService: AuthorizationService,
    private depositSettingsService: DepositSettingsService,
    private router: Router
  ) {
   
  }

  async ngOnInit(): Promise<void> {
    this.sectionsObj = JSON.parse(this.sections);
    this.showNewLogo = new Date() >= this.rebrandDate;

    if (this.topBarLoaded) {
      return;
    }
    try {
      await this.injectHealthcareTopBar();

      let topBar = await initializeHealthcareTopBarV2(
        $("#healthcareTopBar"),
        environment.auth0.clientId,
        environment.auth0.domain,
        environment.auth0.refreshToken,
        environment.auth0.scope,
        null,
        null
      );

      await topBar.initialize();
      window["healthcareTopBar"] = topBar;
      this.authorizationService.setHealthcareTopBar(topBar);
      await this.authorizationService.waitUntilReady();
      this.topBarLoaded = true;
      this.globalFilterService.filtersLoaded.subscribe(() => {
        this.filtersChanged();
      });

      this.globalFilterService.clientEnvDataSourceSelectionChanged.subscribe(
        () => {
          this.filtersChanged();
        }
      );
      this.setValidSections();
     

      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentRoute = routes.componentFromRoute(
            _.replace(event.url, "/", "")
          );
        }
      });

      this.authorizationService.HealthcareTopBar.notifyActivity();
    } catch (error) {
      console.log(error);
    }
  }

  private setValidSections(): void {
    this.validSections = this.sectionsObj.filter(
      (section) =>
        !section.subSection &&
        this.authorizationService.hasFeature(section.feature, true)
    );
  }

  refreshRoute() {
    if (this.currentRoute) {
      let routePath = "/" + this.currentRoute.path;
      // Known hack: https://stackoverflow.com/questions/47813927/how-to-refresh-a-component-in-angular
      this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
        this.router.navigate([routePath]);
      });
    }
  }

  public get showHeader(): boolean {
    return this.topBarLoaded;
  }

  // allDataSources is a collection of libraries that we can connect to iSeries with.
  // As long as allDataSources is null then we assume that the API has not returned.
  // We should use this boolean to show a label telling the user that we are currently loading.
  public get showLoadingFilter(): boolean {
    return !this.globalFilterService.allDataSources;
  }

  /// If we have more than one data source then show the user so they can switch sources if they wish.
  public get showClientFilter(): boolean {
    return (
      this.globalFilterService.allDataSources &&
      Object.keys(this.globalFilterService.allDataSources).length > 1
    );
  }

  /// If we have exactly one client then we will want to show a label.
  public get showSingleClientLabel(): boolean {
    return (
      this.globalFilterService.allDataSources &&
      Object.keys(this.globalFilterService.allDataSources).length === 1
    );
  }

  // clientEnvironments contain the environment(s) available for the selected client within "allDataSources".
  // If this is an environment without a parent then the clientEnvironments.length will be 1.
  // If it is a parent, it will be 1 or greater.  We only want to show the clientEnvironments when more
  // than one data source (iSeries library) is avaiable for a given client selection.
  public get showDataSourceFilter(): boolean {
    return (
      this.globalFilterService.clientEnvironments &&
      this.globalFilterService.clientEnvironments.length > 1
    );
  }

  // Show a "Client Setup Incomplete" If the length of allDataSources is 0.  This makes the logical assumption that the user has
  // has not been granted a client library that was available in iSeries.
  public get showNoClientsAvailable(): boolean {
    return (
      this.globalFilterService.allDataSources &&
      Object.keys(this.globalFilterService.allDataSources).length === 0
    );
  }

  public get selectedClientDataSources(): PrismDataSource[] {
    return this.globalFilterService.clientEnvironments;
  }

  public get selectedClientDataSource(): string {
    return this.globalFilterService.selectedEnvironment.ClientId3Digit;
  }

  public get isSelectedEnvironmentAvailable(): boolean {
    if (!this.globalFilterService.selectedEnvironment) {
      return false;
    }

    return (
      this.globalFilterService.selectedEnvironment.DataSourceStatus ===
      DB2DataSourceAvailability.AVAILABLE
    );
  }

  public get hasSettings(): boolean {
    if (this.topBarLoaded) {
      return this.authorizationService.showSettingsIcon;
    }
  }

  filtersChanged() {
    this.depositSettingsService.getDepositSettings(
      this.globalFilterService.selectedEnvironment.ClientId4Digit
    );
    this.checkEnvironmentUnavailable();
  }

  checkValidLink(name: string, feature: string): boolean {
    if (!feature || !name) {
      return false;
    }
    if (!this.authorizationService.hasFeature(feature)) {
      return false;
    }
    if (name === LOCKBOX_SECTION) {
      if (this.depositSettingsService.currentDepositSettings) {
        let state =
          this.depositSettingsService.currentDepositSettings.Settings
            .DepositOption !== LOCKBOX;
        return state;
      }
    }
    return true;
  }

  public get selectedClientName(): string {
    return !this.globalFilterService.clientEnvironments
      ? "(None)"
      : this.globalFilterService.clientEnvironments[0].Description;
  }

  public get clients(): string[] {
    return Object.keys(this.globalFilterService.allDataSources);
  }

  public get allDataSources(): { [client: string]: PrismDataSource[] } {
    return this.globalFilterService.allDataSources;
  }

  public checkEnvironmentUnavailable(): void {
    let state =
      !this.globalFilterService.selectedEnvironmentIsAvailable &&
      !this.globalFilterService.userDismissedUnavailableEnvironmentDialog;
    this.showEnvironmentMessage = state;
  }

  public get clientSelections(): any[] {
    if (this._clientSelections != null) {
      return this._clientSelections;
    }

    const result = new Array();

    for (const key in this.globalFilterService.allDataSources) {
      if (key === null) {
        continue;
      }
      const item = this.globalFilterService.allDataSources[
        key
      ] as PrismDataSource[];
      result.push({
        description: item[0].Description,
        id: item[0].ClientId4Digit,
      });
    }

    this._clientSelections = result;
    return result;
  }

  private onClientDataSourceSelectionChanged(
    dataSourceSelection: PrismDataSource
  ): void {
    if (dataSourceSelection) {
      this.globalFilterService.selectedClientId =
        dataSourceSelection.ClientId4Digit;
      this.globalFilterService.selectedEnvironmentId =
        dataSourceSelection.ClientId3Digit;
      this.globalFilterService.clientDataSourceSelectionChanged.emit(
        dataSourceSelection
      );
      this.depositSettingsService.getDepositSettings(
        dataSourceSelection.ClientId4Digit
      );
    }
    if (!this.authorizationService.HealthcareTopBar) {
      return;
    }
    this.authorizationService.HealthcareTopBar.notifyActivity();
    this.refreshRoute();
  }

  private onClientEnvDataSourceSelectionChanged(
    dataSourceSelection: PrismDataSource
  ): void {
    if (dataSourceSelection) {
      this.globalFilterService.selectedEnvironment = dataSourceSelection;
      this.globalFilterService.clientDataSourceSelectionChanged.emit(
        dataSourceSelection
      );
      this.depositSettingsService.getDepositSettings(
        dataSourceSelection.ClientId4Digit
      );
    }
    if (!this.authorizationService.HealthcareTopBar) {
      return;
    }
    this.authorizationService.HealthcareTopBar.notifyActivity();
    this.refreshRoute();
  }

  public hideWarningDialog() {
    this.globalFilterService.userDismissedUnavailableEnvironmentDialog = true;
  }
  private injectHealthcareTopBar(): Promise<any> {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.async = true;
      script.src = environment.healthcareTopBar;
      script.addEventListener("load", resolve);
      script.addEventListener("error", () => reject("Error loading script."));
      script.addEventListener("abort", () => reject("Script loading aborted."));
      document.body.appendChild(script);
    });
  }
  public onSettingsClick(): void {
    this.authorizationService.HealthcareTopBar.notifyActivity();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/header/header.component.ts